/**
 * Import batches — one id per import run (items.import_batch_id, mig 751).
 *
 * Every import run mints one batch id and stamps it on each item it creates,
 * so the done screen can (a) list exactly those items ("View these N items" →
 * the items list filtered by `?importBatch=<id>`) and (b) undo the whole run
 * by retiring them through the governed `retire_new_item` path.
 *
 * Duplicate-skip on retry: re-running an import of the same source into the
 * same workspace skips rows that already landed in an earlier batch that was
 * not retired. The match key is the source's own id when the importer has
 * one (CSV `external_id`, a Jira/Notion/Linear/Miro record id via
 * entity_external_ids), else title + source.
 */

import type { Item } from '../types/prioritization/lifecycle';

/** URL param the items list reads to show one import run. */
export const IMPORT_BATCH_PARAM = 'importBatch';

export function newImportBatchId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback only for very old runtimes; shape-compatible with uuid.
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/** Items-list link that shows exactly one import run. */
export function importBatchListHref(batchId: string): string {
  return `/prioritization/items?${IMPORT_BATCH_PARAM}=${encodeURIComponent(batchId)}`;
}

type ImportedItemLike = Pick<Item, 'id' | 'title' | 'source'> & {
  importBatchId?: string | null;
  import_batch_id?: string | null;
  exit_status?: string | null;
  external_id?: string | null;
  externalId?: string | null;
};

export function itemImportBatchId(item: ImportedItemLike): string | null {
  return item.importBatchId ?? item.import_batch_id ?? null;
}

/** An item that an earlier import created and that is still in play. */
export function isLiveImportedItem(item: ImportedItemLike): boolean {
  return !!itemImportBatchId(item) && (item.exit_status ?? 'active') !== 'archived';
}

/** Normalised title + source key, for rows with no source id. */
export function titleSourceKey(title: string | null | undefined, source: string | null | undefined): string {
  const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  return `${norm(title)}␟${norm(source)}`;
}

export interface PriorImportIndex {
  /** external_id values on live imported items. */
  externalIds: Set<string>;
  /** title+source keys of live imported items. */
  titleSourceKeys: Set<string>;
  /** Ids of live imported items (for connector-id lookups). */
  itemIds: Set<string>;
}

/**
 * Index the items that earlier import runs created and that were not retired.
 * Items with no batch id (created by hand, or before batches existed) never
 * count — dedupe only protects against re-running an import.
 */
export function buildPriorImportIndex(items: readonly ImportedItemLike[]): PriorImportIndex {
  const index: PriorImportIndex = { externalIds: new Set(), titleSourceKeys: new Set(), itemIds: new Set() };
  for (const item of items) {
    if (!isLiveImportedItem(item)) continue;
    index.itemIds.add(item.id);
    const ext = item.external_id ?? item.externalId;
    if (ext && String(ext).trim()) index.externalIds.add(String(ext).trim());
    index.titleSourceKeys.add(titleSourceKey(item.title, item.source));
  }
  return index;
}

/**
 * True when this row already landed in an earlier, un-retired import.
 * A row with a source id is matched on that id only; title + source is the
 * fallback for rows that have none.
 */
export function wasAlreadyImported(
  index: PriorImportIndex,
  row: { externalId?: string | null; title?: string | null; source?: string | null },
): boolean {
  const ext = row.externalId?.trim();
  if (ext) return index.externalIds.has(ext);
  if (!row.title?.trim()) return false;
  return index.titleSourceKeys.has(titleSourceKey(row.title, row.source));
}

/**
 * For connector imports whose source id lives in entity_external_ids (not on
 * the item): given the connector's external→internal mapping, return the
 * external ids whose mapped item is a live imported item.
 */
export function externalIdsAlreadyImported(
  index: PriorImportIndex,
  mappings: ReadonlyArray<{ externalId: string; internalId: string }>,
): Set<string> {
  const out = new Set<string>();
  for (const m of mappings) {
    if (index.itemIds.has(m.internalId)) out.add(m.externalId);
  }
  return out;
}

/** "K skipped as already imported" — empty when nothing was skipped. */
export function describeSkippedAsImported(skipped: number): string {
  if (skipped <= 0) return '';
  return `${skipped} skipped as already imported`;
}

export interface ImportRowFailure {
  rowNumber: number;
  title: string;
  reason: string;
}

/**
 * Save import rows one at a time, awaiting each, and count only rows the
 * saver confirms. `save` returns null to skip a row (not counted either way);
 * a refusal or a throw becomes a failure carrying its reason. `savedIds[i]` is
 * the id the saver reported for row i, when it saved and reported one.
 */
export async function saveImportRows<T>(
  rows: readonly T[],
  save: (row: T, index: number) => Promise<{ ok: boolean; id?: string; reason?: string } | null>,
  titleOf: (row: T) => string,
): Promise<{ created: number; failures: ImportRowFailure[]; savedIds: Array<string | undefined> }> {
  let created = 0;
  const failures: ImportRowFailure[] = [];
  const savedIds: Array<string | undefined> = [];
  for (const [index, row] of rows.entries()) {
    try {
      const result = await save(row, index);
      if (result === null) continue;
      if (result.ok) {
        created++;
        savedIds[index] = result.id;
      } else failures.push({ rowNumber: index + 1, title: titleOf(row), reason: result.reason ?? 'The item could not be saved' });
    } catch (err) {
      failures.push({
        rowNumber: index + 1,
        title: titleOf(row),
        reason: err instanceof Error ? err.message : 'The item could not be saved',
      });
    }
  }
  return { created, failures, savedIds };
}

/** What an import callback reports back: rows it confirmed saved, and why the rest did not. */
export interface ImportSaveOutcome {
  created: number;
  failures: ImportRowFailure[];
  /** Rows deliberately not created (already imported / already exist). */
  skipped?: number;
  /** `savedIds[i]` is the item saved for the i-th row handed over, when one was. */
  savedIds?: ReadonlyArray<string | undefined>;
}

export interface ImportOutcomeNotice {
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
  duration: number;
}

/**
 * The toast for one import run, built from what actually saved — never from
 * how many creates were attempted. A run that saved nothing but had failures
 * is an error; a run with some failures is a warning; success only when every
 * attempted row saved. (Counting attempts announced "11 items created" beside a
 * "not saved" warning on the 2026-09-04 Miro demo.)
 */
export function summarizeImportOutcome(outcome: {
  created: number;
  failures: ReadonlyArray<Pick<ImportRowFailure, 'reason'>>;
  skipped?: number;
  /** Existing records the run updated (they count as saved). */
  updated?: number;
  /** Singular noun for what was created. Default "item". */
  noun?: string;
  /** Appended after "N items created", e.g. " and added to intake". */
  destination?: string;
  /** Names the run in failure titles, e.g. "Miro import". Default "Import". */
  label?: string;
  /** Title when every row saved. Default "<label> complete". */
  successTitle?: string;
  /** Extra detail shown only when nothing failed (e.g. a zone breakdown). */
  note?: string;
  /** True when the done screen lists each failed row with its reason. */
  failuresListed?: boolean;
}): ImportOutcomeNotice {
  const { created, failures } = outcome;
  const failed = failures.length;
  const noun = outcome.noun ?? 'item';
  const label = outcome.label ?? 'Import';
  const skipped = outcome.skipped ?? 0;
  const updated = outcome.updated ?? 0;
  const landed = created + updated;
  const skippedNote = describeSkippedAsImported(skipped);

  const parts = [
    `${created} ${noun}${created !== 1 ? 's' : ''} created${outcome.destination ?? ''}${updated ? `, ${updated} updated` : ''}${failed ? `, ${failed} failed` : ''}.`,
  ];
  if (skippedNote) parts.push(`${skippedNote}.`);
  if (failed) {
    const firstReason = failures[0]?.reason?.trim();
    if (firstReason) parts.push(firstReason.endsWith('.') ? firstReason : `${firstReason}.`);
    if (outcome.failuresListed) parts.push('The failed rows are listed with the reason.');
  } else if (outcome.note) {
    parts.push(outcome.note);
  }
  const message = parts.join(' ');

  if (failed > 0) {
    return landed === 0
      ? { type: 'error', title: `${label} failed`, message, duration: 9000 }
      : { type: 'warning', title: `${label} finished with errors`, message, duration: 9000 };
  }
  if (landed === 0 && skipped === 0) {
    return { type: 'warning', title: 'Nothing imported', message: `No ${noun}s were found to import.`, duration: 6000 };
  }
  return { type: 'success', title: outcome.successTitle ?? `${label} complete`, message, duration: 5000 };
}
